import { userAtom } from '@/global-store';
import useLogoutFn from '@/hooks/useLogoutFn';
import { T } from '@/i18n/T';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import LoadingButton from '@components/Button/LoadingButton';
import PasswordField from '@components/ChangePasswordForm/PasswordField';
import { useChangePasswordMutation } from '@http/queries';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useNotification,
} from '@eye-able/ui';
import type { ChangePasswordFormValues } from '@components/ChangePasswordForm/types';
import { getNewPasswordError } from '@components/ChangePasswordForm/validation';

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}

const FORM_OPTIONS = {
  defaultValues: {
    currentPassword: '',
    newPassword: '',
    newPasswordRepeat: '',
  },
  mode: 'onSubmit',
  reValidateMode: 'onSubmit',
} as const;

function ChangePasswordDialog({
  open,
  onOpenChange,
}: ChangePasswordDialogProps) {
  const { t } = useInlineTranslation();
  const [user] = useAtom(userAtom);
  const logoutFn = useLogoutFn();
  const notification = useNotification();
  const changePasswordMutation = useChangePasswordMutation();
  const form = useForm<ChangePasswordFormValues>(FORM_OPTIONS);
  const [logoutPending, setLogoutPending] = useState(false);

  const shouldShowCurrentPassword = Boolean(user?.hasPassword);

  useEffect(() => {
    form.reset(FORM_OPTIONS.defaultValues);
    setLogoutPending(false);
  }, [form, open]);

  useEffect(() => {
    if (!logoutPending) return;

    const timeoutId = window.setTimeout(() => {
      void logoutFn().catch(() => {
        setLogoutPending(false);
        notification.error(t('errorTryAgain'));
      });
    }, 800);

    return () => window.clearTimeout(timeoutId);
  }, [logoutFn, logoutPending, notification, t]);

  if (!user) {
    return null;
  }

  const onSubmit = async ({
    currentPassword,
    newPassword,
  }: ChangePasswordFormValues): Promise<void> => {
    setLogoutPending(false);
    form.clearErrors('currentPassword');

    try {
      await changePasswordMutation.mutateAsync({
        currentPassword,
        newPassword,
        token: null,
      });

      notification.success(t('passwordChangeSuccess'));
      setLogoutPending(true);
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? String(error.message)
          : '';

      if (errorMessage === 'Invalid password') {
        form.setError('currentPassword', {
          type: 'server',
          message: errorMessage,
        });
        return;
      }

      notification.error(t('errorTryAgain'));
    }
  };

  const isBusy = changePasswordMutation.isPending || logoutPending;

  const validateNewPassword = (value: string): string | true =>
    getNewPasswordError(value, t) ?? true;

  const validateNewPasswordRepeat = (value: string): string | true => {
    const passwordError = getNewPasswordError(value, t);

    if (passwordError) {
      return passwordError;
    }

    return value === form.getValues('newPassword')
      ? true
      : t('passwordsDontMatch');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent maxWidth='sm' className='eye:w-full' persistent={isBusy}>
        <DialogTitle>
          <T k='changePassword' />
        </DialogTitle>
        {!shouldShowCurrentPassword ? (
          <Alert variant='info'>
            <T k='noPasswordYet' />
          </Alert>
        ) : null}
        <FormProvider {...form}>
          <form onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}>
            <div className='eye:flex eye:flex-col eye:gap-xs'>
              {shouldShowCurrentPassword ? (
                <PasswordField
                  form={form}
                  name='currentPassword'
                  label={t('currentPassword')}
                  autoComplete='current-password'
                  required
                  autoFocus
                  disabled={isBusy}
                  onValueChange={() => {
                    form.clearErrors('currentPassword');
                  }}
                />
              ) : null}

              <PasswordField
                form={form}
                name='newPassword'
                label={t('newPassword')}
                autoComplete='new-password'
                required
                autoFocus={!shouldShowCurrentPassword}
                disabled={isBusy}
                validate={validateNewPassword}
              />

              <PasswordField
                form={form}
                name='newPasswordRepeat'
                label={t('newPasswordRepeat')}
                autoComplete='new-password'
                required
                disabled={isBusy}
                validate={validateNewPasswordRepeat}
              />
            </div>
            <DialogActions>
              <Button
                type='button'
                variant='outline'
                disabled={isBusy}
                onClick={() => {
                  onOpenChange?.(false);
                }}
              >
                {t('cancel')}
              </Button>
              <LoadingButton type='submit' variant='yellow' loading={isBusy}>
                {t('changePassword')}
              </LoadingButton>
            </DialogActions>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}

export default ChangePasswordDialog;
